import {createSlice} from '@reduxjs/toolkit';
import {
    getReservationsData,
    getReservationsDailyCountsData,
    getReservationsDailySegmentedData,
    getReservationsDailyData,
    resetSegementedState,
    setTransientLegend,
    setEventLegend,
    setMonthlyLegend,
    setReservationsFilters,
    setEmployeeLegend,
    setCompLegend,
} from './reservations-actions';
import dayjs from 'utils/dayjs';
import {displayDateFormat} from 'hooks/config';

const now = dayjs().format(displayDateFormat);
const endDate = dayjs().add(7, 'day').format(displayDateFormat);

export const initialState = {
    allReservations: {
        reservations: {},
        isLoading: false,
        filters: {
            dateFrom: now,
            dateTo: endDate,
            selectedType: [],
            selectedStatus: [
                {
                    label: 'Valid Reservations',
                    value: 'valid',
                },
            ],
        },
    },
    dailyCounts: {
        counts: {},
        isLoading: true,
    },
    dailySegmented: {
        segments: {},
        legend: {
            transient: true,
            monthly: true,
            event: true,
            employee: true,
            comp: true,
        },
    },
    daily: {
        reservations: {},
        isLoading: false,
    },
};

const reservationsSlice = createSlice({
    name: 'reservations',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getReservationsData.fulfilled, (state, {payload}) => {
                state.allReservations.reservations = payload;
                state.allReservations.isLoading = false;
            })
            .addCase(getReservationsData.pending, (state) => {
                state.allReservations.isLoading = true;
            })
            .addCase(getReservationsData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }

                state.allReservations.isLoading = false;
            })
            .addCase(getReservationsDailyData.fulfilled, (state, {payload}) => {
                state.daily.reservations = payload;
                state.daily.isLoading = false;
            })
            .addCase(getReservationsDailyData.pending, (state) => {
                state.daily.isLoading = true;
            })
            .addCase(getReservationsDailyData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }

                state.daily.isLoading = false;
            })
            .addCase(
                getReservationsDailySegmentedData.fulfilled,
                (state, {payload}) => {
                    state.dailySegmented.segments = payload;
                }
            )
            .addCase(
                getReservationsDailyCountsData.fulfilled,
                (state, {payload}) => {
                    state.dailyCounts.counts = payload;
                    state.dailyCounts.isLoading = false;
                }
            )
            .addCase(getReservationsDailyCountsData.pending, (state) => {
                state.dailyCounts.isLoading = true;
            })
            .addCase(
                getReservationsDailyCountsData.rejected,
                (state, action) => {
                    if (action.meta.aborted) {
                        return;
                    }

                    state.dailyCounts.isLoading = false;
                }
            )
            .addCase(resetSegementedState, (state) => {
                state.dailySegmented.segments =
                    initialState.dailySegmented.segments;
            })
            .addCase(setTransientLegend, (state) => {
                state.dailySegmented.legend.transient =
                    !state.dailySegmented.legend.transient;
            })
            .addCase(setEventLegend, (state) => {
                state.dailySegmented.legend.event =
                    !state.dailySegmented.legend.event;
            })
            .addCase(setMonthlyLegend, (state) => {
                state.dailySegmented.legend.monthly =
                    !state.dailySegmented.legend.monthly;
            })
            .addCase(setEmployeeLegend, (state) => {
                state.dailySegmented.legend.employee =
                    !state.dailySegmented.legend.employee;
            })
            .addCase(setCompLegend, (state) => {
                state.dailySegmented.legend.comp =
                    !state.dailySegmented.legend.comp;
            })
            .addCase(setReservationsFilters, (state, {payload}) => {
                state.allReservations.filters = payload;
            });
    },
});

export default reservationsSlice.reducer;
